import React from "react"
import { Link, useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import FeatureTick from "@/components/FeatureTick"
import AISparkle from "@/components/AISparkle"
import HelpTooltip from "@/components/HelpTooltip"
import PlanCardPrice from "@/components/PlanCardPrice"
import FeatureCross from "@/components/FeatureCross"
import { gtmSignupStart, gtmTracking } from "@/utils/gtm-tracking"
import { PLAN_BUTTON_DEFAULTS } from "@/config/planButtonDefaults"

const PlanCard = ({
  tier,
  frequency,
  highlight = false,
  onSelect,
  snapshot = false,
  onSeeAllFeaturesSelect,
  discount = 0,
  showFreeAsColumn = false,
  signupVariant = "",
  planButtonText,
  useShortDescription = false,
  shortAiBadge = false,
}) => {
  const { t } = useTranslation()
  const fullDescription = t(`plans.${tier}.description`)
  const description = useShortDescription
    ? t(`plans.${tier}.description-signup-mobile`, {
        defaultValue: fullDescription,
      })
    : fullDescription
  const featureChecklist = Object.values(
    t(`plans.${tier}.checklist`, { returnObjects: true }),
  )

  const limitFreeTestIncluded = Object.values(
    t(`plans.${tier}.limit-free-test-included`, { returnObjects: true }),
  )
  const limitFreeTestNotIncluded = Object.values(
    t(`plans.${tier}.limit-free-test-not-included`, { returnObjects: true }),
  )

  const aiChecklist = t(`plans.${tier}.ai-checklist`, { returnObjects: true })
  const hasAiChecklist =
    typeof aiChecklist === "object" &&
    aiChecklist !== null &&
    !Array.isArray(aiChecklist) &&
    Object.keys(aiChecklist).length > 0

  const isLimitFreeVariant = ["signup_base"].includes(signupVariant)
  const tierHeadingClassName = "h5"

  const snapshotButtonText =
    (planButtonText && planButtonText[tier]) ||
    t(PLAN_BUTTON_DEFAULTS.snapshot[tier])
  const standardButtonText =
    (planButtonText && planButtonText[tier]) ||
    t(PLAN_BUTTON_DEFAULTS.standard[tier])

  const renderFeatureText = feature => {
    if (typeof feature === "string") return feature

    const betaLabel = feature.beta ? (
      <span className="tag">{t("feature-table.labels.beta")}</span>
    ) : null

    if (feature.description && feature.title) {
      return (
        <>
          <HelpTooltip content={feature.description}>
            {feature.title}
          </HelpTooltip>
          {betaLabel}
        </>
      )
    }

    return (
      <>
        {feature.title}
        {betaLabel}
      </>
    )
  }

  const renderAiBadge = modifierClass => {
    if (tier === "free" || tier === "starter") return null

    const aiBadgeCopy = shortAiBadge
      ? t("plans.ai-badge-short")
      : t("plans.ai-badge")
    const className = ["plan-card__ai-badge", modifierClass]
      .filter(Boolean)
      .join(" ")

    return <span className={className}>{aiBadgeCopy}</span>
  }

  return snapshot ? (
    <div className="plan-card__container plan-card__container--snapshot">
      <div className="plan-card plan-card--snapshot">
        <div className="plan-card__tier">
          <div className="mb-0 h4">
            {t(`plans.name.${tier}`)}
            {renderAiBadge()}
          </div>
        </div>
        <p className="plan-card__description">
          {t(`plans.${tier}.description-snapshot`)}
        </p>
        <div className="plan-card__snapshot-footer">
          <PlanCardPrice
            tier={tier}
            frequency={frequency}
            discount={discount}
            showFreeAsColumn={showFreeAsColumn}
            compactPrice={shortAiBadge}
          />
          <div className="plan-card__snapshot-cta mt-1 mb-1">
            <button
              className="btn"
              onClick={() => {
                gtmSignupStart(tier)()
                onSelect()
              }}
            >
              {snapshotButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className={`plan-card ${highlight ? "plan-card--highlight" : ""}`}>
      {highlight && (
        <span className="plan-card__highlight-pill">
          {t("plans.highlight")}
        </span>
      )}
      <div className="plan-card__tier plan-card__tier--icon">
        <img src={`/icons/signup/${tier}.svg`} alt="" role="presentation" />
        {renderAiBadge("plan-card__ai-badge--icon")}
      </div>
      <div className="plan-card__tier plan-card__tier--name">
        <div className={`mb-0 ${tierHeadingClassName}`}>
          {t(`plans.name.${tier}`)}
          {renderAiBadge("plan-card__ai-badge--name")}
        </div>
      </div>
      <p className="p--sm plan-card__description">{description}</p>
      <PlanCardPrice
        tier={tier}
        frequency={frequency}
        discount={discount}
        showFreeAsColumn={showFreeAsColumn}
        compactPrice={shortAiBadge}
      />
      <button
        className={`btn ${!highlight ? "btn--tertiary" : ""} ${tier === "free" ? "mb-12 md:mb-16" : ""}`}
        onClick={() => {
          gtmSignupStart(tier)()
          onSelect()
        }}
      >
        {standardButtonText}
      </button>
      {tier !== "free" && (
        <p className="mb-0 mt-3 mb-4 md:mb-8 p--xs text-centered plan-card__try-free">
          {t("signup.14-day-trial-no-card-required")}
        </p>
      )}

      <div className="plan-card__features">
        <p className="p--sm mb-3">
          <strong>{t(`plans.${tier}.everything-in`)}</strong>
        </p>
        {tier === "free" && isLimitFreeVariant ? (
          <>
            <ul>
              {limitFreeTestIncluded.map(feature => (
                <FeatureTick key={feature} className="gap-2">
                  {feature}
                </FeatureTick>
              ))}
            </ul>
            {hasAiChecklist && (
              <ul>
                {Object.values(aiChecklist).map(feature => (
                  <AISparkle
                    key={feature.title ? feature.title : feature}
                    className="gap-1"
                  >
                    {renderFeatureText(feature)}
                  </AISparkle>
                ))}
              </ul>
            )}
            <span className="divider divider--dashed"></span>
            <ul>
              {limitFreeTestNotIncluded.map(feature => (
                <FeatureCross key={feature} className="gap-2">
                  {renderFeatureText(feature)}
                </FeatureCross>
              ))}
            </ul>
          </>
        ) : tier === "starter" && isLimitFreeVariant ? (
          <>
            <ul>
              {featureChecklist.map(feature => (
                <FeatureTick
                  key={feature.title ? feature.title : feature}
                  className="gap-2"
                >
                  {renderFeatureText(feature)}
                </FeatureTick>
              ))}
            </ul>
            {hasAiChecklist && (
              <ul>
                {Object.values(aiChecklist).map(feature => (
                  <AISparkle
                    key={feature.title ? feature.title : feature}
                    className="gap-2"
                  >
                    {renderFeatureText(feature)}
                  </AISparkle>
                ))}
              </ul>
            )}
            <span className="divider divider--dashed mt-5 mb-5"></span>
            <ul>
              {limitFreeTestNotIncluded.map(feature => (
                <FeatureCross key={feature} className="gap-2">
                  {feature}
                </FeatureCross>
              ))}
            </ul>
          </>
        ) : (
          <>
            <ul>
              {featureChecklist.map(feature => (
                <FeatureTick
                  key={feature.title ? feature.title : feature}
                  className="gap-2"
                >
                  {renderFeatureText(feature)}
                </FeatureTick>
              ))}
            </ul>
            {hasAiChecklist && (
              <ul>
                {Object.values(aiChecklist).map(feature => (
                  <AISparkle
                    key={feature.title ? feature.title : feature}
                    className="gap-2"
                  >
                    {renderFeatureText(feature)}
                  </AISparkle>
                ))}
              </ul>
            )}
          </>
        )}
        {/* See all features */}
        {onSeeAllFeaturesSelect &&
          (typeof onSeeAllFeaturesSelect === "function" ? (
            <button
              className="mb-0 p--xs text-centered text-link text-link--bare plan-card__see-all-features"
              onClick={() => {
                gtmTracking("Link Click", "See all features", `${tier} plan`)()
                onSeeAllFeaturesSelect()
              }}
            >
              {t("plans.see-all-features")}
            </button>
          ) : (
            <Link
              to={onSeeAllFeaturesSelect}
              className="mb-0 p--xs text-centered text-link text-link--bare plan-card__see-all-features"
              onClick={() =>
                gtmTracking("Link Click", "See all features", `${tier} plan`)()
              }
            >
              {t("plans.see-all-features")}
            </Link>
          ))}
      </div>
    </div>
  )
}

export default PlanCard
